import { useState, type FormEvent } from 'react';
import { MarcaIcone } from '@shared/components/ui/MarcaIcone';
import { api, rotas } from '@shared/lib/api';
import { authConfigurado, supabase } from '@shared/auth/supabase';
import { SeletorPais } from '@shared/components/ui/SeletorPais';
import { OUTRO, PAIS_PADRAO, acharPais } from '@shared/components/ui/paises';
import { lerIntencao, rotaDaIntencao } from '@shared/lib/intencao';
import { Reenvio } from './Reenvio';
import s from './Login.module.css';

/**
 * Entrar e criar conta, na mesma tela.
 *
 * Quem clica em "Contratar" na lamina publica cai aqui, e a maioria
 * dessas pessoas AINDA NAO TEM CONTA -- mandar para um login puro seria
 * um beco: a tela pediria uma senha que elas nunca criaram.
 *
 * Os dois caminhos da plataforma, e por que so um deles esta aqui:
 *
 *   CLIENTE   cadastra-se sozinho, agora, neste formulario. E a maioria.
 *   PROVEDOR  e CONVIDADO pelo admin. Os papeis dele (estrategista,
 *             gestor) e a esteira de aprovacao sao decisao da
 *             plataforma, nao escolha de quem preenche um formulario.
 *             Ele chega por /definir-acesso, com o link do convite.
 *
 * Por isso o cadastro daqui cria SEMPRE um cliente, e a API recusa um
 * email ja convidado com uma mensagem que aponta para o link do convite.
 *
 * O CADASTRO NAO ENTRA DIRETO (desde 14/10/2026): a conta nasce com o
 * e-mail por confirmar, e o login so passa depois do link. A tela vira
 * "Confirme seu e-mail", com o reenvio a mao. Antes a conta nascia
 * confirmada e entrava na hora -- e cadastros com e-mail descartavel
 * apareceram em producao.
 */

/** Mensagens do Supabase traduzidas para o que o usuario pode fazer. */
function explica(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) {
    return 'Email ou senha incorretos.';
  }
  if (m.includes('email not confirmed')) {
    return 'Falta confirmar seu e-mail. Abra o link que enviamos quando '
         + 'você criou a conta, ou peça outro abaixo.';
  }
  if (m.includes('failed to fetch') || m.includes('networkerror')) {
    return 'Nao foi possivel falar com o Supabase. Verifique a conexao.';
  }
  return msg;
}

/** So digitos, no maior tamanho que o pais aceita. Mesmo criterio do
 *  cadastro do provedor: mascara local varia demais entre paises. */
function limpaTel(v: string, iso: string): string {
  const max = Math.max(...acharPais(iso).tamanhos);
  return v.replace(/\D/g, '').slice(0, max);
}

type Modo = 'entrar' | 'criar';

export function Login() {
  const [modo, setModo] = useState<Modo>('entrar');

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [paisTel, setPaisTel] = useState(PAIS_PADRAO);
  const [ddiManual, setDdiManual] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');

  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // O login foi recusado por falta de confirmacao: abre o reenvio.
  const [naoConfirmado, setNaoConfirmado] = useState(false);
  // Cadastro feito: o e-mail para onde foi o link. Troca a tela inteira.
  const [aguardando, setAguardando] = useState<string | null>(null);

  // A contratacao que trouxe a pessoa ate aqui. Vai no link do e-mail,
  // porque ele abre em outra aba -- onde a intencao guardada nesta nao
  // existe (ver lib/intencao.ts).
  const intencao = lerIntencao();
  const destino = intencao ? rotaDaIntencao(intencao) : null;

  const criando = modo === 'criar';

  /** Troca de aba sem levar junto o erro da outra: "Email ou senha
   *  incorretos" em cima do formulario de cadastro nao faz sentido. */
  function trocarModo(novo: Modo) {
    setModo(novo);
    setErro(null);
    setNaoConfirmado(false);
  }

  async function entrar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setNaoConfirmado(false);
    setEnviando(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
      if (error) {
        setErro(explica(error.message));
        setNaoConfirmado(error.message.toLowerCase().includes('email not confirmed'));
      }
      // Sucesso nao navega daqui: o onAuthStateChange no ProvedorSessao
      // recarrega /api/auth/me e o router leva para o modulo certo.
    } catch (err) {
      setErro(explica((err as Error).message));
    } finally {
      setEnviando(false);
    }
  }

  async function criar(e: FormEvent) {
    e.preventDefault();
    setErro(null);

    // Conferido aqui e nao no servidor: a confirmacao existe para pegar
    // erro de digitacao, e mandar os dois campos ao servidor so daria uma
    // segunda chance de divergirem no caminho.
    if (senha !== confirma) {
      setErro('As senhas não conferem.');
      return;
    }
    if (senha.length < 8) {
      setErro('A senha precisa de pelo menos 8 caracteres.');
      return;
    }

    setEnviando(true);
    try {
      const ddi = paisTel === OUTRO ? ddiManual : acharPais(paisTel).ddi;
      await api.post(rotas.cadastroCliente, {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
        telefone: `+${ddi}${telefone}`,
        senha,
        destino,
      });
      // Nao entra: o Auth so aceita o login depois do link. A senha fica
      // no estado para o reenvio, que a exige.
      setAguardando(email.trim().toLowerCase());
    } catch (err) {
      // ErroApi ja traz a frase da API em `message` (ver shared/lib/api).
      setErro((err as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  if (aguardando) {
    return (
      <div className={`${s.tela} shell`}>
        <div className={s.cartao}>
          <div className={s.marca}>
            <MarcaIcone tamanho={32} className={s.ponto} />
            <span className={s.nome}>Heavy Trade</span>
          </div>

          <div className={s.envelope} aria-hidden>
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
                 strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
              <path d="m3.5 7 8.5 6 8.5-6" />
            </svg>
          </div>
          <h1 className={s.titulo}>Confirme seu e-mail</h1>
          <p className={s.texto}>
            Enviamos um link para <b>{aguardando}</b>. Abra o e-mail e toque
            em <b>Confirmar meu e-mail</b> para ativar sua conta.
            {intencao ? ' Depois disso você volta direto para a contratação.' : ''}
          </p>
          <p className={s.dica}>
            O link vale por 24 horas. Não chegou em alguns minutos? Confira o
            spam ou peça outro.
          </p>

          <Reenvio email={aguardando} senha={senha} destino={destino} />

          <div className={s.rodape}>
            Já confirmou?{' '}
            <button type="button" className={s.link}
                    onClick={() => { setAguardando(null); trocarModo('entrar'); }}>
              Entrar
            </button>
            {' · '}
            <button type="button" className={s.link}
                    onClick={() => { setAguardando(null); setErro(null); }}>
              Usar outro e-mail
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.tela} shell`}>
      <div className={s.cartao}>
        <div className={s.marca}>
          <MarcaIcone tamanho={32} className={s.ponto} />
          <span className={s.nome}>Heavy Trade</span>
        </div>

        <div className={s.abas} role="tablist">
          {(['entrar', 'criar'] as const).map((m) => (
            <button key={m} type="button" role="tab"
                    aria-selected={modo === m}
                    className={modo === m ? `${s.aba} ${s.abaAtiva}` : s.aba}
                    onClick={() => trocarModo(m)}>
              {m === 'entrar' ? 'Entrar' : 'Criar conta'}
            </button>
          ))}
        </div>

        <h1 className={criando ? `${s.titulo} ${s.tituloSozinho}` : s.titulo}>
          {criando ? 'Criar conta' : 'Entrar'}
        </h1>
        {!criando && (
          <div className={s.sub}>Acesse com o email cadastrado na plataforma.</div>
        )}

        <form onSubmit={criando ? criar : entrar}>
          {criando && (
            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="nome">Nome</label>
              <input
                id="nome" className={s.entrada} type="text" required
                minLength={2} autoComplete="name" value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Seu nome completo"
              />
            </div>
          )}

          <div className={s.campo}>
            <label className={s.rotulo} htmlFor="email">Email</label>
            <input
              id="email" className={s.entrada} type="email" required
              autoComplete={criando ? 'email' : 'username'} value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
            />
          </div>

          {criando && (
            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="tel">Telefone</label>
              <div className={paisTel === OUTRO ? s.telLinhaOutro : s.telLinha}>
                <SeletorPais valor={paisTel} aoMudar={(iso) => {
                  setPaisTel(iso);
                  // Trocar de pais pode encurtar o limite: reaplica.
                  setTelefone((t) => limpaTel(t, iso));
                }} />
                {paisTel === OUTRO && (
                  <input className={s.entrada} required inputMode="numeric"
                         value={ddiManual}
                         onChange={(e) =>
                           setDdiManual(e.target.value.replace(/\D/g, '').slice(0, 4))}
                         placeholder="DDI" aria-label="Código do país" />
                )}
                <input id="tel" className={s.entrada} required inputMode="numeric"
                       autoComplete="tel-national" value={telefone}
                       onChange={(e) => setTelefone(limpaTel(e.target.value, paisTel))}
                       placeholder={acharPais(paisTel).exemplo} />
              </div>
            </div>
          )}

          <div className={s.campo}>
            <label className={s.rotulo} htmlFor="senha">Senha</label>
            <input
              id="senha" className={s.entrada} type="password" required
              autoComplete={criando ? 'new-password' : 'current-password'}
              minLength={criando ? 8 : undefined}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
          </div>

          {criando && (
            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="confirma">Confirmar senha</label>
              <input
                id="confirma" className={s.entrada} type="password" required
                autoComplete="new-password" value={confirma}
                onChange={(e) => setConfirma(e.target.value)}
              />
            </div>
          )}

          <button className={s.botao} type="submit" disabled={enviando}>
            {enviando
              ? (criando ? 'Criando conta…' : 'Entrando...')
              : (criando ? 'Criar conta' : 'Entrar')}
          </button>
        </form>

        {erro && <div className={s.erro}>{erro}</div>}
        {naoConfirmado && !criando && (
          <Reenvio email={email.trim().toLowerCase()} senha={senha} destino={destino} />
        )}

        {!authConfigurado && (
          <div className={s.aviso}>
            Supabase nao configurado no front: defina VITE_SUPABASE_URL e
            VITE_SUPABASE_ANON_KEY em frontend/.env.
          </div>
        )}

        <div className={s.rodape}>
          {criando ? (
            <>
              Já tem conta?{' '}
              <button type="button" className={s.link}
                      onClick={() => trocarModo('entrar')}>Entrar</button>
            </>
          ) : (
            <>
              Ainda não tem conta?{' '}
              <button type="button" className={s.link}
                      onClick={() => trocarModo('criar')}>Criar conta</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
